import { FC, ReactNode, useEffect, useState } from 'react';
import { Box, BoxProps, Tooltip, useTheme } from '@mui/material';
import { SystemIcon, SystemIconNames } from '@monorepo/nova-icons';
import {
  SidebarContainer,
  SidebarContent,
  SidebarIconButton,
  SidebarIconButtonAccent,
  SidebarSection,
} from './sidebar.styles';

export interface SidebarItemProps {
  icon: SystemIconNames;
  onClick?: () => void;
  accent?: boolean;
  tooltip?: string;
  children?: ReactNode;
  badge?: number | string;
  iconImage?: string;
  // Internal props (set by parent)
  isActive?: boolean;
  onActivate?: () => void;
}

export interface SidebarArrayItem
  extends Omit<SidebarItemProps, 'isActive' | 'onActivate'> {
  content?: ReactNode;
}

export interface SidebarProps extends Omit<BoxProps, 'children'> {
  topItems?: SidebarArrayItem[];
  bottomItems?: SidebarArrayItem[];
  // Dimensions
  collapsedWidth?: string | number;
  expandedWidth?: string | number;
  // Styling
  backgroundColor?: string;
  expandedBackgroundColor?: string;
  iconColumnBackgroundColor?: string;
  borderRadius?: string | number;
  boxShadow?: string | number;
  padding?: string | number;
  // State
  initialActiveIndex?: number | null;
  onIndexChange?: (index: number | null) => void;
  // Advanced
  sx?: BoxProps['sx'];
}

export const SidebarItem: FC<SidebarItemProps> = ({
  icon,
  onClick,
  accent = false,
  tooltip,
  children,
  badge,
  iconImage,
  isActive = false,
  onActivate,
}) => {
  const ButtonComponent = accent ? SidebarIconButtonAccent : SidebarIconButton;
  const theme = useTheme();

  const handleClick = () => {
    onClick?.();
    if (children && onActivate) {
      onActivate();
    }
  };

  // Add special classes for specific icons
  const isPencil = icon === 'Pencil';
  const isHeart = icon === 'Heart' || icon === 'HeartFilled';

  // For heart icon, switch between outline and filled based on active state
  const displayIcon = isHeart && isActive ? 'HeartFilled' : icon;

  const getClassName = () => {
    const classes = [];
    if (isActive) classes.push('active');
    if (isPencil && isActive) classes.push('pencil-active');
    if (isHeart) classes.push('heart-icon');
    return classes.join(' ');
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        alignItems: 'center',
        display: 'inline-flex',
      }}
    >
      <Tooltip title={tooltip || ''} placement="left" arrow>
        <ButtonComponent onClick={handleClick} className={getClassName()}>
          {iconImage ? (
            <Box
              sx={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                outline: isActive
                  ? theme.palette.mode === 'dark'
                    ? '1.5px solid #000000'
                    : '1.5px solid #FFFFFF'
                  : theme.palette.mode === 'dark'
                    ? '1.5px solid #DDFD6C'
                    : `1.5px solid ${theme.palette.primary.main}`,
                backgroundColor: isActive
                  ? theme.palette.mode === 'dark'
                    ? '#DDFD6C'
                    : theme.palette.primary.main
                  : 'transparent',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                transition: theme.transitions.create(
                  ['outline', 'background-color'],
                  {
                    duration: theme.transitions.duration.short,
                  },
                ),
              }}
            >
              <Box
                component="img"
                src={iconImage}
                alt={tooltip || 'icon'}
                sx={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                }}
              />
            </Box>
          ) : (
            <SystemIcon
              iconName={displayIcon}
              size={24}
              color={isActive ? 'opposite' : accent ? 'primary' : undefined}
            />
          )}
        </ButtonComponent>
      </Tooltip>
      {badge !== undefined && badge !== null && badge !== 0 && (
        <Box
          sx={{
            textAlign: 'center',
            justifyContent: 'center',
            display: 'flex',
            flexDirection: 'column',
            color: isActive
              ? theme.palette.sidebar.iconActive
              : theme.palette.text.secondary,
            fontSize: '12px',
            fontFamily: 'General Sans',
            fontWeight: '400',
            lineHeight: '16.8px',
            letterSpacing: '0.06px',
            wordWrap: 'break-word',
            transition: theme.transitions.create(['color'], {
              duration: theme.transitions.duration.short,
            }),
          }}
        >
          {typeof badge === 'number' && badge > 99 ? '99+' : badge}
        </Box>
      )}
    </Box>
  );
};

SidebarItem.displayName = 'SidebarItem';

export const Sidebar: FC<SidebarProps> = ({
  topItems = [],
  bottomItems = [],
  collapsedWidth = '80px',
  expandedWidth = '380px',
  borderRadius = '16px',
  padding: _padding,
  initialActiveIndex = null,
  onIndexChange,
  sx,
  ...rest
}) => {
  const theme = useTheme();
  const [activeIndex, setActiveIndex] = useState<number | null>(
    initialActiveIndex,
  );
  const [expandedContent, setExpandedContent] = useState<ReactNode>(() => {
    if (initialActiveIndex !== null) {
      const allItems = [...topItems, ...bottomItems];
      return allItems[initialActiveIndex]?.content || null;
    }
    return null;
  });

  // Sync expanded content when topItems/bottomItems change (for dynamic content updates)
  useEffect(() => {
    if (activeIndex !== null) {
      const allItems = [...topItems, ...bottomItems];
      const newContent = allItems[activeIndex]?.content || null;
      setExpandedContent(newContent);
    }
  }, [topItems, bottomItems, activeIndex]);

  const handleItemActivate = (index: number, content: ReactNode) => {
    if (activeIndex === index) {
      setActiveIndex(null);
      setExpandedContent(null);
      onIndexChange?.(null);
    } else {
      setActiveIndex(index);
      setExpandedContent(content);
      onIndexChange?.(index);
    }
  };

  const renderSidebarItem = (item: SidebarArrayItem, index: number) => {
    return (
      <SidebarItem
        key={index}
        icon={item.icon}
        onClick={item.onClick}
        accent={item.accent}
        tooltip={item.tooltip}
        badge={item.badge}
        iconImage={item.iconImage}
        isActive={activeIndex === index}
        onActivate={
          item.content
            ? () => handleItemActivate(index, item.content)
            : undefined
        }
      >
        {item.content}
      </SidebarItem>
    );
  };

  const isExpanded = activeIndex !== null;

  // Parse borderRadius prop to extract individual corner values
  const parseBorderRadius = (radius: string | number) => {
    const radiusStr = String(radius);
    const values = radiusStr.split(' ').map((v) => v.trim());

    if (values.length === 1) {
      // Single value: all corners
      return {
        topLeft: values[0],
        topRight: values[0],
        bottomRight: values[0],
        bottomLeft: values[0],
      };
    } else if (values.length === 4) {
      // Four values: top-left, top-right, bottom-right, bottom-left (CSS standard order)
      return {
        topLeft: values[0],
        topRight: values[1],
        bottomRight: values[2],
        bottomLeft: values[3],
      };
    }
    // Default fallback
    return {
      topLeft: '15px',
      topRight: '15px',
      bottomRight: '15px',
      bottomLeft: '15px',
    };
  };

  const radiusValues = parseBorderRadius(borderRadius);
  console.log(radiusValues);
  return (
    <SidebarContainer
      {...rest}
      expanded={isExpanded}
      sx={{
        width: isExpanded ? expandedWidth : collapsedWidth,
        // Don't override backgroundColor here - let SidebarContainer handle it based on theme
        backgroundColor: 'transparent',
        borderRadius: borderRadius,
        ...sx,
      }}
    >
      {/* Expanded Content - appears on the left */}
      <SidebarContent
        expanded={isExpanded}
        sx={{
          // Use MUI theme palette for background color
          backgroundColor: isExpanded
            ? theme.palette.sidebar.contentExpanded
            : 'transparent',
          padding: isExpanded ? 6 : 0,
          // Use parsed border radius values for left corners
          borderTopLeftRadius: radiusValues.topLeft,
          borderBottomLeftRadius: radiusValues.bottomLeft,
        }}
      >
        {expandedContent}
      </SidebarContent>

      {/* Icon Column - stays on the right */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: collapsedWidth,
          flexShrink: 0,
          // Use MUI theme palette for background color
          backgroundColor: isExpanded
            ? theme.palette.sidebar.iconsExpanded
            : theme.palette.sidebar.collapsed,
          // Use parsed border radius values
          borderTopRightRadius: radiusValues.topRight,
          borderBottomRightRadius: radiusValues.bottomRight,
          borderTopLeftRadius: isExpanded ? 0 : radiusValues.topLeft,
          borderBottomLeftRadius: isExpanded ? 0 : radiusValues.bottomLeft,
        }}
      >
        {/* Top Section */}
        {topItems.length > 0 && (
          <SidebarSection>
            {topItems.map((item, idx) => renderSidebarItem(item, idx))}
          </SidebarSection>
        )}

        {/* Spacer to push bottom items down */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Bottom Section */}
        {bottomItems.length > 0 && (
          <SidebarSection>
            {bottomItems.map((item, idx) =>
              renderSidebarItem(item, topItems.length + idx),
            )}
          </SidebarSection>
        )}
      </Box>
    </SidebarContainer>
  );
};

Sidebar.displayName = 'Sidebar';
